import axios from '@/plugins/axios';
import { generateFilterParams, getStatus, mapByState } from '../helpers.js';

function expandedStatesFilter(currentState, item) {
  const { states } = currentState.filters;
  if (states && states.length > 0) {
    return states.includes(item.states[0].state);
  }
  return item;
}

function expandedStatusFilter(currentState, item) {
  const { status } = currentState.filters;
  if (status) {
    return item.status.replace(' ', '') === status;
  }
  return item;
}

function expandedAdminFilter(currentState, item) {
  const { administrators } = currentState.filters;
  if (administrators) {
    return item.administrators.toLowerCase().includes(administrators.toLowerCase());
  }
  return item;
}

function expandedExpirationDateFilter(currentState, item) {
  const { expiresAt } = currentState.filters;
  if (expiresAt) {
    if (item.expiresAt && item.expiresAt !== '') {
      const dates = expiresAt.split('>').map((x) => new Date(x) || null);
      const result = (!isNaN(dates[0]) ? dates[0] <= new Date(item.expiresAt) : true)
        && (!isNaN(dates[1]) ? dates[1] > new Date(item.expiresAt) : true);
      return result;
    } else {
      return false;
    }
  }
  return item;
}

const sortByName = (a, b) => {
  const nameA = a?.name?.toUpperCase();
  const nameB = b?.name?.toUpperCase();
  let comparison = 0;
  if (nameA > nameB) {
    comparison = 1;
  } else if (nameA < nameB) {
    comparison = -1;
  }
  return comparison;
};

const initialState = () => ({
  items: [],
  itemsLoading: false,
  lawyersLoading: false,
  pagination: {
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  },
  filters: {},
  totalItems: 0,
  legacyMode: true,
  lawyers: [],
});

const state = {
  ...initialState(),
};

const mutations = {
  setLegacyMode: (currentState, payload) => {
    currentState.legacyMode = payload;
  },
  clear: (currentState) => {
    Object.assign(currentState, initialState());
  },
  setItems: (currentState, { items, totalItems }) => {
    currentState.items = items;
    currentState.totalItems = totalItems;
  },
  setPagination: (currentState, payload) => {
    currentState.pagination = payload;
  },
  setFilters: (currentState, payload) => {
    currentState.filters = { ...payload };
  },
  setItemsLoading: (currentState, payload) => {
    currentState.itemsLoading = payload;
  },
  setLawyersLoading: (currentState, payload) => {
    currentState.lawyersLoading = payload;
  },
  setLawyers: (currentState, payload) => {
    currentState.lawyers = [...payload];
  },
};

const actions = ({
  getItems({ commit }) {
    return new Promise((resolve) => {
      commit('setItemsLoading', true);
      const params = {
        page: state.pagination.page,
        rowsPerPage: state.pagination.itemsPerPage,
        sortBy: state.pagination.sortBy[0],
        descending: state.pagination.sortDesc[0],
        ...generateFilterParams(state.filters),
      };
      axios.get('/submissions', { params })
        .then((response) => {
          commit('setItems', response.data);
          resolve();
        }).finally(() => commit('setItemsLoading', false));
    });
  },
  setPagination({ commit, dispatch }, paginationInfo) {
    commit('setPagination', paginationInfo);
    dispatch('getItems');
  },
  setFilters({ commit, dispatch }, filters) {
    state.pagination.page = 1;
    commit('setFilters', filters);
    dispatch('getItems');
  },
  getLawyers({ commit }) {
    return new Promise((resolve) => {
      commit('setLawyersLoading', true);
      axios.get('users?filters[userType]=Lawyer,Administrator', {}).then((response) => {
        const lawyers = response.data.items;
        lawyers.sort(sortByName);
        lawyers.unshift({ id: 'Unassigned', name: 'Unassigned', isActive: true });
        commit('setLawyers', lawyers);
        resolve();
      }).finally(() => commit('setLawyersLoading', false));
    });
  },
  updateAssignedLawyer({ dispatch }, { submission, lawyer }) {
    return new Promise((resolve, reject) => {
      // clean the submission id in case it has extra info
      const cleanId = submission.id.substring(0, 24);
      const url = `/submissions/${cleanId}`;
      axios.get(url)
        .then((response) => {
          const dbSubmission = response.data;
          if (lawyer.id === 'Unassigned') {
            dbSubmission.assignedLawyer = null;
          } else {
            dbSubmission.assignedLawyer = { id: lawyer.id, name: lawyer.name };
          }
          axios.put(url, dbSubmission)
            .then(() => {
              dispatch('getItems');
              resolve();
            }).catch(reject);
        }).catch(reject);
    });
  },
});

const getters = {
  itemsLoading(currentState) {
    return currentState.itemsLoading;
  },
  lawyersLoading(currentState) {
    return currentState.lawyersLoading;
  },
  downloadUrl(currentState) {
    const filters = generateFilterParams(currentState.filters);
    return `${axios.defaults.baseURL}submissions/download?${
      [currentState.legacyMode ? 'mode=expanded' : 'mode=compact',
        ...Object.keys(filters)
          .map((key) => `${key}=${filters[key]}`),
      ].join('&')}`;
  },
  tableItems(currentState) {
    const sMap = (submission) => {
      let effectiveAt = submission.effectiveAt?.substring(0, 10);
      if (submission.status !== 'Reviewed' && submission.effectiveOnApproval) {
        effectiveAt = 'On Approval';
      }

      if (submission.states?.every((st) => st.status === 'Rejected')) {
        effectiveAt = '';
      }

      let reviewedAt = submission.reviewedAt?.substring(0, 10);
      if (submission.status !== 'Reviewed') {
        reviewedAt = '';
      }

      let assignedLawyerName = 'Unassigned';
      if (submission.assignedLawyer?.id) {
        const lawyer = currentState.lawyers?.find((l) => l.id === submission.assignedLawyer.id);
        // only change value of assignedLawyerName if lawyer was found in the lawyers collection
        if (lawyer) {
          assignedLawyerName = lawyer.userType === 'Administrator' ? `${lawyer.name} (Admin)` : lawyer.name;
        } else if (currentState.lawyersLoading) {
          /* this conditional should rarely if ever display in the UI, but in the event that
           the API call to getUsers hung up for several seconds for some reason, 'Loading...'
           is more accurate than 'Unassigned' to display temporarily */
          assignedLawyerName = 'Loading...';
        }
      }

      return {
        ...submission,
        dueAt: submission.dueAt?.substring(0, 10),
        effectiveAt,
        reviewedAt,
        submittedAt: submission.submittedAt?.substring(0, 10),
        submittedBy: submission.submittedBy?.name,
        assignedLawyer: assignedLawyerName,
        assignedLawyerId: submission.assignedLawyer?.id,
        lenderFlag: submission.lenderFlag ?? submission.flags?.some((f) => f.type === 'Lender'),
      };
    };

    const hasLawChange = (submission, st) => submission.flags?.some((f) => f.type === 'LawChange' && st.state === f.meta?.state);

    return currentState.legacyMode
      ? [...currentState.items.flatMap((submission) => submission
        .states.map((st) => ({
          ...sMap(submission),
          id: `${submission.id}-${st.state}`,
          states: [{
            ...st,
            status: getStatus(submission, st),
            lawChange: hasLawChange(submission, st) || false,
          }],
          administrators: st.administratorName,
          expiresAt: submission.status === 'Reviewed' && st.status === 'Approved'
            ? st.expiresAt?.substring(0, 10) || ''
            : '',
          effectiveAt: st.status === 'Rejected' ? '' : sMap(submission).effectiveAt,
          status: getStatus(submission, st),
        })))]
        .filter((x) => expandedStatusFilter(currentState, x))
        .filter((x) => expandedAdminFilter(currentState, x))
        .filter((x) => expandedExpirationDateFilter(currentState, x))
        .filter((x) => expandedStatesFilter(currentState, x))
      : [...currentState.items.map((submission) => ({
        ...sMap(submission),
        states: submission.states.map((st) => ({
          ...st,
          status: getStatus(submission, st),
          lawChange: hasLawChange(submission, st) || false,
        })),
        administrators: mapByState(submission, (st) => st.administratorName, ''),
        expiresAt: submission.status === 'Reviewed'
          ? mapByState(submission, (st) => (st.status === 'Approved' ? st.expiresAt?.substring(0, 10) : ''), '')
          : '',
        status: mapByState(submission, (st) => getStatus(submission, st)),
      }))];
  },
};

export default {
  getStatus,
  mapByState,
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
